/**
 * SIL-1346 - buyer-verification copy shared by the gate, the verify banner,
 * the status pill and the verification modal. Wording from
 * docs/exchange/design-handoff/README.md sections 1 and 5, and spec
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md sections 4.5
 * and 5.
 *
 * Same discipline as `activeVerification.ts`: the sentences live here, not in
 * the components. The gate callout in particular is rendered by the
 * verification modal but chosen by `useVerificationGate`, and the pending
 * copy appears both as a banner state and as the gate's toast - three
 * surfaces, one string each.
 */

/**
 * The info callout the verification modal shows when a gated action (Buy now,
 * Make an offer, a bid) opened it rather than the user clicking "Get
 * verified". Also the user-facing message for the API's
 * `BUYER_VERIFICATION_REQUIRED` code - `errors.ts` reads it from here so the
 * callout and the toast can never say two different things.
 */
export const BUYER_GATE_CALLOUT = 'Buying requires a verified buyer account.';

/**
 * A `pending` org gets this toast INSTEAD of the modal (spec section 5):
 * reopening a form they have already submitted reads as "your submission was
 * lost". Same wording as the banner's pending state below, deliberately.
 */
export const BUYER_GATE_PENDING_TOAST_TITLE = 'Buyer verification in review';
export const BUYER_GATE_PENDING_TOAST_BODY =
  'List prices, Active Verification results and chain of custody open as soon as review completes.';

/** Verify banner, unverified state (handoff section 1). Hidden when verified. */
export const BUYER_VERIFY_BANNER_TITLE = 'Exact prices and Passport details are shared with verified buyers';
export const BUYER_VERIFY_BANNER_BODY =
  'Listings show indicative price ranges. Verify your entity once to see list prices, buy, make offers and open each node’s Silicon Passport.';
export const BUYER_VERIFY_BANNER_CTA = 'Get verified';
/** SIL-1559: shown instead of the CTA above when a saved draft already exists. */
export const BUYER_VERIFY_BANNER_CTA_CONTINUE = 'Continue verification';

/** Verify banner, pending state: same copy as the gate toast, and no button. */
export const BUYER_VERIFY_BANNER_PENDING_TITLE = BUYER_GATE_PENDING_TOAST_TITLE;
export const BUYER_VERIFY_BANNER_PENDING_BODY = BUYER_GATE_PENDING_TOAST_BODY;

/** Verification modal chrome (handoff section 5). */
export const BUYER_VERIFICATION_MODAL_TITLE = 'Buyer verification';
export const BUYER_VERIFICATION_MODAL_SUBTITLE =
  'A one-time review of the purchasing entity. Approval unlocks exact prices, Buy now, offers and full Passport details on every listing.';

/** Shown to a non-admin member in place of the form (spec section 4.5). */
export const BUYER_VERIFICATION_MEMBER_NOTE = 'Ask your org admin to complete buyer verification.';

/* --------------------------------------------------------------------------
 * SIL-1350 - the 4-step form itself. Handoff section 5's table, verbatim where
 * it spells a sentence out. Every label, hint, button and message the modal
 * renders is a constant here: the modal file is layout and behaviour only, the
 * same discipline `ActiveVerificationExplainerModal` follows.
 * ----------------------------------------------------------------------- */

/**
 * "Signed in as {email} · account created {date}" (handoff section 5). A
 * function rather than a bare string because the sentence, not the
 * concatenation order, is the copy - a component assembling three fragments is
 * how the separator drifts between surfaces. Falls back to the email alone
 * when the API sends no usable `accountCreatedAt`.
 */
export function buyerVerificationIdentityLine(email: string, accountCreatedAt: string): string {
  const created = accountCreatedAt ? new Date(accountCreatedAt) : null;
  const date =
    created && !Number.isNaN(created.getTime())
      ? created.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : null;
  return date ? `Signed in as ${email} · account created ${date}` : `Signed in as ${email}`;
}

/** Stepper labels, in order. Index + 1 is the 1-based step number everywhere. */
export const BUYER_VERIFICATION_STEPS = [
  'Entity',
  'Ownership and authority',
  'Mobile',
  'Agreement and review',
] as const;

/** Footer buttons. Cancel becomes Back after step 1; Continue becomes Submit on step 4. */
export const BUYER_VERIFICATION_CANCEL = 'Cancel';
export const BUYER_VERIFICATION_BACK = 'Back';
export const BUYER_VERIFICATION_CONTINUE = 'Continue';
export const BUYER_VERIFICATION_SUBMIT = 'Submit for verification';
export const BUYER_VERIFICATION_SAVING = 'Saving…';
/** SIL-1559: inline autosave status beside the footer buttons (no per-keystroke toasts). */
export const BUYER_VERIFICATION_AUTOSAVE_SAVING = 'Saving…';
export const BUYER_VERIFICATION_AUTOSAVE_SAVED = 'Draft saved';
export const BUYER_VERIFICATION_AUTOSAVE_ERROR = "Couldn't save - retrying";
export const BUYER_VERIFICATION_SUBMITTING = 'Submitting…';

/** Step 1 - Entity. */
export const BUYER_VERIFICATION_LEGAL_NAME_LABEL = 'Legal entity name';
export const BUYER_VERIFICATION_ENTITY_TYPE_LABEL = 'Entity type';
export const BUYER_VERIFICATION_COUNTRY_LABEL = 'Country of incorporation';
export const BUYER_VERIFICATION_COUNTRY_HINT = 'Two-letter country code, for example US';
export const BUYER_VERIFICATION_REGISTRATION_NUMBER_LABEL = 'Registration number';
export const BUYER_VERIFICATION_REGISTRATION_NUMBER_HINT = 'EIN or company number';
export const BUYER_VERIFICATION_WEBSITE_LABEL = 'Website';
export const BUYER_VERIFICATION_ADDRESS_LABEL = 'Registered address';

/** Entity-type options, in the handoff's order. Values are the DTO's enum. */
export const BUYER_VERIFICATION_ENTITY_TYPE_LABELS: Record<string, string> = {
  ai_lab: 'AI lab',
  neocloud: 'Neocloud / GPU cloud',
  enterprise: 'Enterprise',
  research: 'Research institution',
  reseller: 'Reseller / broker',
};

/** Step 2 - Ownership and authority. */
export const BUYER_VERIFICATION_SIGNATORY_NAME_LABEL = 'Authorized signatory';
export const BUYER_VERIFICATION_SIGNATORY_NAME_HINT =
  "The person legally authorized to sign purchase agreements for this entity, such as an officer, director or managing member. We send the SMS code to this person's mobile.";
export const BUYER_VERIFICATION_SIGNATORY_TITLE_LABEL = 'Title';
export const BUYER_VERIFICATION_OWNERS_LABEL = 'Beneficial owners 25%+';
export const BUYER_VERIFICATION_OWNERS_HINT = 'Optional for listed companies. One name per line.';
export const BUYER_VERIFICATION_VOLUME_LABEL = 'Expected annual purchase volume';
export const BUYER_VERIFICATION_PRIMARY_USE_LABEL = 'Primary use';
export const BUYER_VERIFICATION_AUTHORITY_CHECKBOX =
  'The signatory has authority to bind the entity to purchase agreements.';

/** Purchase-volume options, in the handoff's order. Values are the DTO's enum. */
export const BUYER_VERIFICATION_VOLUME_LABELS: Record<string, string> = {
  under_1m: 'Under $1M',
  '1m_10m': '$1M - $10M',
  '10m_50m': '$10M - $50M',
  '50m_100m': '$50M - $100M',
  over_100m: 'Over $100M',
};

/** Step 2 - the document row. */
export const BUYER_VERIFICATION_DOCUMENT_LABEL = 'Certificate of incorporation / good standing';
export const BUYER_VERIFICATION_DOCUMENT_ATTACH = 'Attach file';
export const BUYER_VERIFICATION_DOCUMENT_REPLACE = 'Replace';
export const BUYER_VERIFICATION_DOCUMENT_HINT = 'PDF, PNG or JPEG, up to 10 MB.';
export const BUYER_VERIFICATION_DOCUMENT_UPLOADING = 'Uploading…';
/** Shown for a document attached in an earlier session: the API stores no file name. */
export const BUYER_VERIFICATION_DOCUMENT_ATTACHED = 'Document attached';
export const BUYER_VERIFICATION_DOCUMENT_TYPE_ERROR = 'Upload a PDF, PNG or JPEG file.';
export const BUYER_VERIFICATION_DOCUMENT_SIZE_ERROR = 'The file must be 10 MB or smaller.';
/** Spec section 6: an S3 upload failure is an inline error with "Try again". */
export const BUYER_VERIFICATION_DOCUMENT_UPLOAD_FAILED = 'The upload did not finish. Try again.';
export const BUYER_VERIFICATION_TRY_AGAIN = 'Try again';

/** Step 3 - Mobile (SMS). */
export const BUYER_VERIFICATION_PHONE_LABEL = 'Signatory mobile';
// SIL-1574: the field is now the SIL-1198 PhoneField (dial-code selector +
// as-you-type national formatting), not a plain international-format text
// box, so the hint no longer tells the user to type a "+1555…" string.
export const BUYER_VERIFICATION_PHONE_HINT = "We'll text a one-time verification code to this number.";
/** SIL-1574: mirrors the API's own PHONE_NOT_SUPPORTED message (buyer-verification.ts) for client-side blur validation. */
export const BUYER_VERIFICATION_PHONE_NOT_SUPPORTED = 'Enter a US or Canadian mobile number.';
/**
 * SIL-1575: `PHONE_UNDELIVERABLE` - Twilio itself rejected the number as
 * permanently undeliverable (invalid, non-mobile, or SMS unsupported for its
 * region) after it passed the `PHONE_NOT_SUPPORTED` allow-list above, so the
 * copy has to say "this number", not repeat the allow-list's "US or Canadian
 * mobile" framing.
 */
export const BUYER_VERIFICATION_PHONE_UNDELIVERABLE =
  "We can't send texts to this number. Use a different mobile number.";
/** SIL-1575: `SMS_PROVIDER_UNAVAILABLE` - our Twilio account/configuration, not the user's number. */
export const BUYER_VERIFICATION_SMS_PROVIDER_UNAVAILABLE =
  'Text verification is temporarily unavailable. Please try again later or contact support.';
/**
 * SIL-1507: SMS opt-in disclosure shown beside the number input (US toll-free
 * registration reviewers check the opt-in screen). Must stay consistent with
 * the SMS section of the Privacy Policy (PrivacyPolicyContent.tsx) and the
 * SMS Terms section of the Terms of Service (TermsOfServiceContent.tsx, SIL-1509).
 */
export const BUYER_VERIFICATION_PHONE_CONSENT =
  'By entering your number you agree to receive a one-time verification code by SMS. Msg & data rates may apply. Reply STOP to opt out.';
export const BUYER_VERIFICATION_TERMS_ROUTE = '/terms';
export const BUYER_VERIFICATION_TERMS_LINK_LABEL = 'Terms of Service';
export const BUYER_VERIFICATION_PRIVACY_ROUTE = '/privacy';
export const BUYER_VERIFICATION_PRIVACY_LINK_LABEL = 'Privacy Policy';
export const BUYER_VERIFICATION_SEND_CODE = 'Send code';
export const BUYER_VERIFICATION_CODE_SENT = 'Code sent';
export const BUYER_VERIFICATION_SENDING_CODE = 'Sending…';
export const BUYER_VERIFICATION_CODE_LABEL = 'Verification code';
export const BUYER_VERIFICATION_VERIFY = 'Verify';
export const BUYER_VERIFICATION_VERIFYING = 'Verifying…';
export const BUYER_VERIFICATION_RESEND = 'Resend code';
/** "Resend in Ns" - the seconds come from the API's `resendAvailableAt`, never a local guess. */
export const buyerVerificationResendIn = (seconds: number) => `Resend in ${seconds}s`;
/** "Sent to •••• •••-0142. The code expires in 10 minutes." - the mask is the API's. */
export const buyerVerificationCodeSentHint = (phoneMasked: string) =>
  `Sent to ${phoneMasked}. The code expires in 10 minutes.`;
export const buyerVerificationPhoneVerified = (phoneMasked: string) =>
  `Mobile number verified · ${phoneMasked}`;

/** Step 4 - Agreement and review. */
export const BUYER_VERIFICATION_REVIEW_HEADING = 'Review';
export const BUYER_VERIFICATION_SCREENING_NOTE =
  'The entity and its beneficial owners are screened against the OFAC SDN list and the BIS Entity List.';
/**
 * Replaces the prototype's "Mutual NDA" (spec section 4.5, change 2 of 2). The
 * version the checkbox accepts is `exchangeUaVersion` from the API, rendered
 * beside the link, so the accepted version is visible rather than implied.
 */
export const BUYER_VERIFICATION_UA_CHECKBOX =
  'I accept the Silicon Exchange User Agreement on behalf of the entity above.';
export const BUYER_VERIFICATION_UA_LINK_LABEL = 'Read the Exchange User Agreement';
/**
 * The versioned agreement route. SIL-1316 (the confidentiality text) is blocked
 * on counsel, not on engineering: the modal links to the route and names the
 * version constant it accepts rather than embedding agreement copy here, so
 * nothing in this file needs re-reviewing when the text lands.
 */
export const BUYER_VERIFICATION_UA_ROUTE = '/exchange-user-agreement';
export const buyerVerificationUaVersionNote = (version: string) => `Version ${version}`;
export const BUYER_VERIFICATION_REVIEW_NOT_PROVIDED = 'Not provided';
export const BUYER_VERIFICATION_REVIEW_MOBILE_LABEL = 'Mobile';
export const BUYER_VERIFICATION_REVIEW_MOBILE_VERIFIED = 'verified by SMS';

/** Submit outcome. */
export const BUYER_VERIFICATION_SUBMITTED_TOAST_TITLE = 'Buyer verification submitted';
export const BUYER_VERIFICATION_SUBMITTED_TOAST_BODY =
  'An ops reviewer checks the entity and you get a notification when the review completes.';

/**
 * Status wording for the read-only views. A member sees the status AND the
 * member note (spec section 4.5), so the status needs a sentence of its own
 * rather than only existing as a pill elsewhere in the app.
 */
export const BUYER_VERIFICATION_STATUS_PREFIX = 'Status';
export const BUYER_VERIFICATION_STATUS_LABELS: Record<string, string> = {
  unverified: 'Not started',
  draft: 'In progress',
  pending: 'Under review',
  verified: 'Verified',
  rejected: 'Not approved',
};

/** Statuses that show a note instead of the form (spec section 4.5). */
export const BUYER_VERIFICATION_PENDING_NOTE =
  'This verification is submitted and under review. You get a notification when the review completes.';
export const BUYER_VERIFICATION_VERIFIED_NOTE =
  'This entity is verified. List prices, offers and full Passport details are open on every listing.';
/** Heading above the `rejectionReason` callout when the modal opens on a rejected record. */
export const BUYER_VERIFICATION_REJECTED_HEADING = 'This verification was not approved';
export const BUYER_VERIFICATION_REJECTED_NO_REASON =
  'The reviewer did not record a reason. Update the details below and submit again.';

/**
 * SIL-1359 — the agreement-only re-acceptance step. Shown instead of the
 * verified-note or the full wizard when a verified org's accepted Exchange
 * User Agreement version has gone stale: no entity, ownership, mobile or
 * document steps re-run, only the agreement itself.
 */
export const BUYER_VERIFICATION_REACCEPTANCE_HEADING = 'The Exchange User Agreement has been updated';
export const BUYER_VERIFICATION_REACCEPTANCE_BODY =
  'Your entity is verified — nothing else changed. Accept the updated agreement to keep buying.';
export const BUYER_VERIFICATION_REACCEPTANCE_ACCEPT = 'Accept and continue';
export const BUYER_VERIFICATION_REACCEPTANCE_ACCEPTING = 'Accepting…';
